// Browser history plumbing, split out of router.js so it can be tested.
//
// Since 2026-09-27 (Mohammed's decision, item 2) Back is plain browser history: one press goes
// back one real entry, like any website. The interceptor that used to redirect a Back out of a
// record (backRedirect), measure and jump to the breadcrumb's parent (stepsBackTo / backPlan /
// navStack) and record per-module entry pages (entryPageUpdate) is gone - it made Back skip
// entries. What is left:
//
//   - the popstate flag, handed to exactly one navigation (consumePopState);
//   - the Back/Forward counter stamped into every entry (withNavIndex / popDirection);
//   - installHistory, which wraps pushState/replaceState for the counter, turns a tab switch's
//     push into a replace when the user's setting asks for it, and tells the breadcrumb which
//     trail the entry a popstate landed on carries (see navTrail.js).

// Reads the popstate flag and clears it, so it applies to exactly one
// navigation. It used to be cleared only when the interceptor acted on it, so
// a Back that landed anywhere else (dashboard -> list, say) left it armed and
// the user's next click on a detail tab was treated as another Back.
export function consumePopState(win) {
  const popState = !!win.popStateDetected
  win.popStateDetected = false
  return popState
}

// Which way a history move went.
//
// `popstate` does not say whether the user pressed Back or Forward, so every entry carries a
// counter, stamped into history.state by [withNavIndex], and the direction is the comparison of
// the entry arrived at with the entry left (window.popStateWentBack).
export const NAV_INDEX = '__mbpNavIndex'

// history.state with the counter added. vue-router keeps its own key in there, so the
// existing state is preserved; a non-object state (older entries) is wrapped.
export function withNavIndex(state, index) {
  const base = state && typeof state === 'object' ? state : (state == null ? {} : { state })
  return { ...base, [NAV_INDEX]: index }
}

// 'back', 'forward', or 'unknown' when an entry predates the counter.
export function popDirection(state, currentIndex) {
  const arrived = state && typeof state === 'object' ? state[NAV_INDEX] : undefined
  if (typeof arrived !== 'number' || typeof currentIndex !== 'number') return 'unknown'
  if (arrived > currentIndex) return 'forward'
  return 'back'
}

// Wraps win.history.pushState / replaceState and listens for popstate. Call once, before the
// router is created, so this listener runs ahead of vue-router's.
//
//   hooks.takePushAsReplace() - true when this push should replace the current entry instead
//                               (a tab switch with "Back button ignores tabs on records" ON).
//                               The entry's state is merged, so its trail and counter survive
//                               until the breadcrumb restamps it.
//   hooks.onPopState(state)   - the state of the entry a Back/Forward landed on.
export function installHistory(win, hooks = {}) {
  const history = win.history
  const nativePushState = history.pushState.bind(history)
  const nativeReplaceState = history.replaceState.bind(history)
  const isObject = value => !!value && typeof value === 'object'

  let navIndex = 0
  let currentNavIndex = 0

  // A replace stays on the same entry, so it keeps that entry's number.
  function replaceState(state, title, url) {
    return nativeReplaceState(withNavIndex(state, currentNavIndex), title, url)
  }

  history.replaceState = replaceState

  history.pushState = function (state, title, url) {
    if (hooks.takePushAsReplace && hooks.takePushAsReplace()) {
      const merged = { ...(isObject(history.state) ? history.state : {}), ...(isObject(state) ? state : {}) }
      return replaceState(merged, title, url)
    }
    navIndex += 1
    currentNavIndex = navIndex
    return nativePushState(withNavIndex(state, currentNavIndex), title, url)
  }

  win.popStateDetected = false
  win.popStateWentBack = true
  win.addEventListener('popstate', event => {
    const direction = popDirection(event.state, currentNavIndex)
    const arrived = isObject(event.state) ? event.state[NAV_INDEX] : undefined
    if (typeof arrived === 'number') currentNavIndex = arrived
    win.popStateDetected = true
    win.popStateWentBack = direction !== 'forward'
    if (hooks.onPopState) hooks.onPopState(event.state)
  })
}
